'use client';

/**
 * Client-side helper que consome o stream SSE de `/api/chat`.
 *
 * Uso:
 *   for await (const chunk of streamChatResponse({ message, pathname })) {
 *     if (chunk.type === 'content') setReply(prev => prev + chunk.content);
 *     else if (chunk.type === 'done') setMeta(chunk.meta);
 *     else if (chunk.type === 'error') setError(chunk.error);
 *   }
 *
 * Diferente do Server Action `sendChatMessage` (que retorna a resposta inteira),
 * essa função emite chunks incrementais — UI atualiza char-by-char.
 */

export type AgentRoleLite = 'vendas' | 'onboarding' | 'suporte';

export interface ChatStreamMeta {
  agentRole: AgentRoleLite;
  toolCallsUsed: number;
  tokensUsed: number;
  finishReason: string | null;
  maxedOut: boolean;
}

export type ChatStreamChunk =
  | { type: 'content'; content: string }
  | { type: 'tool_call'; toolCall: { name: string; arguments: string } }
  | { type: 'done'; meta?: ChatStreamMeta }
  | { type: 'error'; error: string }
  | {
      type: 'ui_block';
      block: {
        kind: 'feedback' | 'report' | 'integration' | 'ticket' | 'handoff';
        payload: Record<string, unknown>;
      };
    };

interface RawChunk {
  type?: string;
  content?: string;
  toolCall?: { name?: string; arguments?: string };
  meta?: {
    agentRole?: string;
    toolCallsUsed?: number;
    tokensUsed?: number;
    finishReason?: string | null;
    maxedOut?: boolean;
  };
  error?: string;
  block?: { kind?: string; payload?: Record<string, unknown> };
}

function normalizeChunk(raw: RawChunk): ChatStreamChunk | null {
  if (raw.type === 'content' && typeof raw.content === 'string') {
    return { type: 'content', content: raw.content };
  }
  if (raw.type === 'tool_call' && raw.toolCall) {
    return {
      type: 'tool_call',
      toolCall: {
        name: raw.toolCall.name ?? '',
        arguments: raw.toolCall.arguments ?? '',
      },
    };
  }
  if (raw.type === 'done') {
    const m = raw.meta;
    if (m && isAgentRoleLite(m.agentRole)) {
      return {
        type: 'done',
        meta: {
          agentRole: m.agentRole,
          toolCallsUsed: m.toolCallsUsed ?? 0,
          tokensUsed: m.tokensUsed ?? 0,
          finishReason: m.finishReason ?? null,
          maxedOut: m.maxedOut ?? false,
        },
      };
    }
    return { type: 'done' };
  }
  if (raw.type === 'error') {
    return { type: 'error', error: raw.error ?? 'Erro desconhecido.' };
  }
  if (raw.type === 'ui_block' && raw.block && typeof raw.block.kind === 'string') {
    const k = raw.block.kind;
    if (
      k === 'feedback' ||
      k === 'report' ||
      k === 'integration' ||
      k === 'ticket' ||
      k === 'handoff'
    ) {
      return { type: 'ui_block', block: { kind: k, payload: raw.block.payload ?? {} } };
    }
  }
  return null;
}

function isAgentRoleLite(v: unknown): v is AgentRoleLite {
  return v === 'vendas' || v === 'onboarding' || v === 'suporte';
}

export async function* streamChatResponse(input: {
  message: string;
  pathname?: string;
  signal?: AbortSignal;
  endpoint?: string;
  visitorId?: string;
}): AsyncGenerator<ChatStreamChunk, void, void> {
  let res: Response;
  try {
    res = await fetch(input.endpoint ?? '/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: input.message,
        ...(input.pathname !== undefined && { pathname: input.pathname }),
        ...(input.visitorId ? { visitorId: input.visitorId } : {}),
      }),
      ...(input.signal ? { signal: input.signal } : {}),
    });
  } catch (err) {
    yield { type: 'error', error: err instanceof Error ? err.message : 'Erro de rede.' };
    return;
  }

  if (!res.body) {
    yield { type: 'error', error: `Sem corpo de resposta (HTTP ${String(res.status)}).` };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE: eventos separados por linha em branco (\n\n). Processa todos os
      // eventos completos no buffer e mantém o resto pra próxima iteração.
      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const rawEvent of events) {
        const lines = rawEvent.split('\n');
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload) continue;
          let parsed: RawChunk;
          try {
            parsed = JSON.parse(payload) as RawChunk;
          } catch {
            continue;
          }
          const chunk = normalizeChunk(parsed);
          if (chunk) yield chunk;
          if (chunk?.type === 'done' || chunk?.type === 'error') {
            // sinais terminais — não vamos esperar mais nada do reader
            return;
          }
        }
      }
    }
  } catch (err) {
    yield { type: 'error', error: err instanceof Error ? err.message : 'Erro de stream.' };
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}
